import Apptimize from '@apptimize/apptimize-web-sdk';
import flatten from 'lodash/flatten';

function localyticsEvent(name, data) {
  window.ll && window.ll('tagEvent', name, data);
}

function hltAnalytics(name, data = {}) {
  localyticsEvent(name, data);
  window.apptimizeSetup && Apptimize.track(name);
}

function dataLayerEvent(event, params = {}) {
  window.dataLayer && window.dataLayer.push({...{'event': event}, ...params});
}

const assetTrayEvents = {
  viewedAssetTray(params) {
    hltAnalytics('Viewed Asset Tray', params);
  },
  closedAssetTray(params) {
    hltAnalytics('Closed Asset Tray', params);
  },
  resizedAssetTray(params) {
    hltAnalytics('Resized Asset Tray', params);
  },
  viewedTabOnAssetTray(params) {
    hltAnalytics('Viewed Tab on Asset Tray', params);
  },
};

const qotdEvents = {
  qotdViewedModal(params) {
    hltAnalytics('Viewed QOTD Modal', params);
  },
  qotdDismissedModal(params) {
    hltAnalytics('Dismissed QOTD Modal', params);
  },
  qotdContinued(params) {
    hltAnalytics('Continued from QOTD', params);
  },
};

const biodigitalEvents = {
  biodigitalDismissedModel(params) {
    hltAnalytics('Dismissed Model', params);
  },
  biodigitalDeselectedObject(params) {
    hltAnalytics('Deselected Object', params);
  },
  biodigitalSelectedObject(params) {
    hltAnalytics('Selected Object', params);
  },
  biodigitalTransitionedChapter(params) {
    hltAnalytics('Transitioned Chapter', params);
  },
  biodigitalViewedModel(params) {
    hltAnalytics('Viewed Model', params);
  },
};

const bookmarkEvents = {
  bookmarkedAsset(bookmark, source) {
    hltAnalytics('Bookmarked Asset', {
      'Source ID': source.id,
      'Source Type': source.type,
      'Asset ID': bookmark.assetId,
      'Asset Title': bookmark.assetTitle,
      'Asset Type': bookmark.assetType,
      'Category ID': bookmark.categoryId,
      'Category Name': bookmark.categoryName,
    });
  },
  removedBookmark(bookmark, source) {
    hltAnalytics('Removed Bookmark', {
      'Source ID': source.id,
      'Source Type': source.type,
      'Asset ID': bookmark.assetId,
      'Asset Title': bookmark.assetTitle,
      'Asset Type': bookmark.assetType,
      'Category ID': bookmark.categoryId,
      'Category Name': bookmark.categoryName,
    });
  },
  flashcardBookmarked({flashcard, category, bookmarked}) {
    hltAnalytics('Bookmark Flashcard', {
      'Flashcard ID': flashcard.id,
      'Category ID': category.id,
      'Category Name': category.name,
      'Bookmarked': bookmarked,
    });
  },
  viewedBookmarkScreen() {
    hltAnalytics('View Bookmarks Screen');
  },
};

const caseStudyEvents = {
  abandonedCaseStudy(grouping, asset) {
    hltAnalytics('Abandoned Case Study', {
      'Grouping ID': grouping?.id,
      'Grouping Title': grouping?.name,
      'Abandoned Asset Path': asset?.path,
      'Abandoned Asset ID': asset?.id,
    });
  },
  completedCaseStudy(grouping) {
    hltAnalytics('Completed Case Study', {
      'Grouping ID': grouping.id,
      'Grouping Title': grouping.name,
    });
  },
  restartedCaseStudy(grouping) {
    hltAnalytics('Restarted Case Study', {
      'Grouping ID': grouping.id,
      'Grouping Title': grouping.name,
    });
  },
  resumedCaseStudy(grouping) {
    hltAnalytics('Resumed Case Study', {
      'Grouping ID': grouping.id,
      'Grouping Title': grouping.name,
    });
  },
  startedCaseStudy(grouping) {
    hltAnalytics('Started Case Study', {
      'Grouping ID': grouping.id,
      'Grouping Title': grouping.name,
    });
  },
};

const catalogEvents = {
  viewedCatalogPage(catalogPage, source) {
    hltAnalytics('Viewed Catalog Page', {
      'Catalog Page ID': catalogPage.id,
      'Catalog Page Name': catalogPage.title,
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
    });
  },
  viewedCatalogHomePage(source) {
    hltAnalytics('Viewed Catalog Home Page', {
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
    });
  },
  selectedAddOn(addOn, source) {
    hltAnalytics('Selected Add On', {
      'Add-On ID': addOn.id,
      'Add-On Name': addOn.title,
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
    });
  },
  viewedAddOnPrompt(addOn, source, answer) {
    hltAnalytics('Viewed Add-On Prompt', {
      'Add-On ID': addOn.id,
      'Add-On Name': addOn.title,
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
      'Answered': answer,
    });
  },
  addedCatalogAddOn(addOn, source) {
    hltAnalytics('Added Catalog Add-On', {
      'Add-On ID': addOn.id,
      'Add-On Name': addOn.title,
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
    });
  },
  disabledAddOn(catalogPage, source, answer) {
    hltAnalytics('Disabled Add-On', {
      'Add-On ID': catalogPage.id,
      'Add-On Name': catalogPage.title,
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
      'Answered': answer,
    });
  },
  viewedAddOnBenefits(catalogPage, marquee) {
    hltAnalytics('Viewed Add-On Benefits', {
      'Add-On ID': catalogPage.id,
      'Add-On Name': catalogPage.title,
      'Benefit Name': marquee.title,
    });
  },
  viewedAddOnReviews(catalogPage) {
    hltAnalytics('Viewed Add-On Reviews', {
      'Add-On ID': catalogPage.id,
      'Add-On Name': catalogPage.title,
    });
  },
  abandonedQuestionnaire(questionnaire, question) {
    hltAnalytics('Abandoned Questionnaire', {
      'Question ID': question.id,
      'Questionnaire ID': questionnaire.id,
    });
  },
};

const categoryEvents = {
  selectedFilter(name) {
    hltAnalytics('Selected Category Tag Filter', {
      'Tag Name': name || 'Unanswered', // Unanswered status has empty display name
    });
  },
  viewedCategory(store, category) {
    const data = {
      'Category ID': category.id,
      'Category Name': category.name,
    };
    let eventName;

    if (store.getters['categoryTypes/terminologyCategoryTypes'].map(ct => ct.id).includes(category.categoryTypeId)) {
      eventName = 'Viewed Terminology Category';
    } else if (store.getters['categoryTypes/multipleChoiceCategoryTypes'].map(ct => ct.id).includes(category.categoryTypeId)) {
      eventName = 'Viewed Question Category';
    } else if (store.getters['categoryTypes/mnemonicCategoryTypes'].map(ct => ct.id).includes(category.categoryTypeId)) {
      eventName = 'Viewed Mnemonic Category';
    }

    if (eventName) {
      hltAnalytics(eventName, data);
    }
  },
};

const checklistEvents = {
  accessedChecklist({ sourceType, sourceId }) {
    hltAnalytics('Accessed Checklist', {
      'Source Type': sourceType,
      'Source ID': sourceId,
    });
  },
  selectedChecklistItem({ name, destinationType, destinationId }) {
    hltAnalytics('Selected Checklist Item', {
      'Item Name': name,
      'Destination Type': destinationType,
      'Destination ID': destinationId,
    });
  },
};

const delayedPurchaseEvents = {
  activatedDelayedPurchase({ source, po }) {
    hltAnalytics('Activated Delayed Purchase', {
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
      'Unrealized Purchase Order ID': po?.id,
      'Unrealized Purchase Order Name': po?.name,
    });
  },
  selectedLaterOnDelayedPurchase({ source, po }) {
    hltAnalytics('Selected Later On Delayed Purchase', {
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
      'Unrealized Purchase Order ID': po?.id,
      'Unrealized Purchase Order Name': po?.name,
    });
  },
  viewedDelayedPurchaseModal({ source, po }) {
    hltAnalytics('Viewed Delayed Purchase Modal', {
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
      'Unrealized Purchase Order ID': po?.id,
      'Unrealized Purchase Order Name': po?.name,
    });
  },
};

const flashcardEvents = {
  answeredFlashcard(store, category = {}, flashcard, answers, answerData, correct, isQOTD, dashboardName) {
    const subscriptionPOs = store.getters['purchaseOrders/subscription'].map(po => po.id);
    const userSubscriptionPO = store.getters['session/currentUser'].purchaseOrders.find(poID => subscriptionPOs.includes(poID));
    const correctAnswers = flashcard.answers.filter(a => a.correct).sort((a, b) => a.id - b.id);
    const userAnswers = flashcard.answers.filter(a => answers.includes(a.id)).sort((a, b) => a.id - b.id);

    const data = {
      'Asset ID': flashcard.id,
      'Category ID': category.id,
      'Category Name': category.name,
      'Answer Was': correct ? 'correct' : 'incorrect',
      'Subscription Purchase Order': userSubscriptionPO,
    };

    if (flashcard.type === 'Matrix Grid') {
      // For matrix grid item types, we need to create an array of answers composed from the
      // answer text as well as the header text for the selected column. The end results should
      // look somehting like
      // ['answer 1: header 1', 'answer 1: header 2', 'answer 2: header 3']
      data['Chosen Answers'] = flatten(flashcard.answers.map(answer => {
        const userAnswer = answerData.matrixGridSelections.find(selection => selection.answerId === answer.id);
        return userAnswer.selections.map((val, idx) => {
          if (val) {
            return `${answer.text}: ${flashcard.matrixGridColumnHeaders[idx + 1]}`;
          }
        }).filter(answer => answer);
      }));
      data['Correct Answers'] = flatten(flashcard.answers.map(answer => {
        return answer.matrixGridCorrect.map((val, idx) => {
          if (val) {
            return `${answer.text}: ${flashcard.matrixGridColumnHeaders[idx + 1]}`;
          }
        }).filter(answer => answer);
      }));
    } else {
      data['Chosen Answer ID'] = userAnswers.map(a => a.id);
      data['Chosen Answers'] = userAnswers.map(a => a.text);
      data['Correct Answers ID'] = correctAnswers.map(a => a.id);
      data['Correct Answers'] = correctAnswers.map(a => a.text);
    }

    if (store.getters['categoryTypes/quizCategoryTypes'].map(ct => ct.id).includes(category.categoryTypeId)) {
      data['Quiz Name'] = category.name;
      data['Quiz ID'] = category.id;
      hltAnalytics('Answered Quiz Question', data);
    } else if (store.getters['categoryTypes/userQuizCategoryType'] && store.getters['categoryTypes/userQuizCategoryType'].id === category.id) {
      data['Quiz Name'] = 'Custom User Quiz';
      data['Quiz ID'] = category.id;
      hltAnalytics('Answered Quiz Question', data);
    } else if (store.getters['categoryTypes/multipleChoiceCategoryTypes'].map(ct => ct.id).includes(category.categoryTypeId)) {
      data['QOTD'] = isQOTD;
      data['Dashboard Name'] = dashboardName;
      hltAnalytics('Answered Practice Question', data);
      // only increment user stats for multiple choice
      store.commit('user/incrementStats');
    }

    if (window.ll) {
      ll('setCustomDimension', 10, store.getters['user/answeredCount']);
    }
  },
  answeredLearningModuleFlashcard(store, flashcard, answers, correct, learningModuleId, isQOTD, dashboardName) {
    const subscriptionPOs = store.getters['purchaseOrders/subscription'].map(po => po.id);
    const userSubscriptionPO = store.getters['session/currentUser'].purchaseOrders.find(poID => subscriptionPOs.includes(poID));
    const correctAnswers = flashcard.answers.filter(a => a.correct).sort((a, b) => a.id - b.id);
    const userAnswers = flashcard.answers.filter(a => answers.includes(a.id)).sort((a, b) => a.id - b.id);

    const data = {
      'Asset ID': flashcard.id,
      'Parent Asset Type': 'Learning Module',
      'Parent Asset ID': learningModuleId,
      'Answer Was': correct ? 'Correct' : 'Incorrect',
      'Chosen Answer ID': userAnswers.map(a => a.id),
      'Chosen Answers': userAnswers.map(a => a.text),
      'Correct Answers ID': correctAnswers.map(a => a.id),
      'Correct Answers': correctAnswers.map(a => a.text),
      'Subscription Purchase Order': userSubscriptionPO,
      'QOTD': isQOTD,
      'Dashboard Name': dashboardName,
    };

    hltAnalytics('Answered Practice Question', data);
  },
  skippedFlashcard(category = {}, flashcard, isTerminology) {
    const data =  {
      'Asset ID': flashcard.id,
      'Category ID': category.id,
      'Category Name': category.name,
    };

    if (isTerminology) {
      hltAnalytics('Skipped Terminology', data);
    } else {
      hltAnalytics('Skipped Practice Question', data);
    }
  },
  taggedFlashcard(store, status, category = {}, flashcard, answers, correct) {
    if (store.getters['categoryTypes/multipleChoiceCategoryTypes'].map(ct => ct.id).includes(category.categoryTypeId)) {
      const subscriptionPOs = store.getters['purchaseOrders/subscription'].map(po => po.id);
      const userSubscriptionPO = store.getters['session/currentUser'].purchaseOrders.find(poID => subscriptionPOs.includes(poID));
      const correctAnswers = flashcard.answers.filter(a => a.correct).sort((a, b) => a.id - b.id);
      const userAnswers = flashcard.answers.filter(a => answers.includes(a.id)).sort((a, b) => a.id - b.id);

      const data = {
        'Asset ID': flashcard.id,
        'Category ID': category.id,
        'Category Name': category.name,
        'Answer Was': correct ? 'Correct' : 'Incorrect',
        'Chosen Answer ID': userAnswers.map(a => a.id),
        'Chosen Answers': userAnswers.map(a => a.text),
        'Correct Answers ID': correctAnswers.map(a => a.id),
        'Correct Answers': correctAnswers.map(a => a.text),
        'Subscription Purchase Order': userSubscriptionPO,
        'Asset Marked As': status,
        // 'Viewed Asset in Rationale': ???
      };

      hltAnalytics('Tagged Practice Question', data);
    }
  },
  viewedTerminology(asset, category = {}) {
    hltAnalytics('Viewed Terminology', {
      'Asset ID': asset.id,
      'Category ID': category.id,
      'Category Name': category.name,
    });
  },
  viewedTerminologyRationale(flashcard, category = {}, status) {
    hltAnalytics('Viewed Terminology Rationale', {
      'Asset ID': flashcard.id,
      'Category ID': category.id,
      'Category Name': category.name,
      'Asset Marked As': status,
      // 'Viewed Asset in Rationale': ???
    });
  },
  viewedExplanation(flashcard, category = {}, timeSpentViewing, correct) {
    hltAnalytics('Viewed Explanation', {
      'Asset ID': flashcard.id,
      'Category ID': category.id,
      'Category Name': category.name,
      'Answered Correctly': correct,
      'Time Spent Viewing': timeSpentViewing,
    });
  },
};

const learningModuleEvents = {
  completedLearningModule(moduleId, moduleTitle) {
    hltAnalytics('Completed Learning Module', {
      'Learning Module ID': moduleId,
      'Learning Module Title': moduleTitle,
    });
  },
  restartedLearningModule(moduleId, moduleTitle) {
    hltAnalytics('Restarted Learning Module', {
      'Learning Module ID': moduleId,
      'Learning Module Title': moduleTitle,
    });
  },
  resumedLearningModule({moduleId, moduleTitle, assetId, assetType}) {
    hltAnalytics('Resumed Learning Module', {
      'Learning Module ID': moduleId,
      'Learning Module Title': moduleTitle,
      'Asset ID': assetId,
      'Asset Type': assetType,
    });
  },
  startedLearningModule(moduleId, moduleTitle) {
    hltAnalytics('Started Learning Module', {
      'Learning Module ID': moduleId,
      'Learning Module Title': moduleTitle,
    });
  },
};

const miscellaneousEvents = {
  apptimizeVariant(experimentName, variantName) {
    hltAnalytics('Apptimize experiment', {
      'Name': experimentName,
      'Name and Variation': `${experimentName}-${variantName}`,
      'Variation': variantName,
    });
  },
  clickedUpsell(store, action, label) {
    dataLayerEvent('TriggerGaEvent', {'category': `${store.getters['app/bundleIdentifier']}`, 'action': `Clicked ${action} Upsell`, 'label': label});
    hltAnalytics('Viewed Upsell');
  },
  clickedSwitchSubscription({ purchaseOrder, status }) {
    hltAnalytics('Clicked on Switch Subscription', {
      'Purchase Order ID': purchaseOrder?.id,
      'Purchase Order Name': purchaseOrder?.name,
      'Status': status,
    });
  },
  ratedBeta(score) {
    hltAnalytics('Rated Beta', {
      'Score': score,
    });
  },
  savedCreditCard() {
    hltAnalytics('Saved Credit Card Clicked');
  },
  selectedBanner({ source, po, text }) {
    hltAnalytics('Selected Banner', {
      'Source ID': source?.id,
      'Source Name': source?.name,
      'Source Type': source?.type,
      'Text': text,
      'Unrealized Purchase Order ID': po?.id,
      'Unrealized Purchase Order Name': po?.name,
    });
  },
  selectedNavItem(navItem, navGroup, widgetType) {
    hltAnalytics('Selected Nav Item', {
      'Nav Item ID': navItem.id,
      'Nav Item Name': navItem.name,
      'Nav Group ID': navGroup.id,
      'Nav Group Name': navGroup.name,
      'Widget Type': widgetType,
    });
  },
  selectedTrialBanner(navItem, trialExpiration, expired) {
    const now = new Date();
    hltAnalytics('Selected Trial Banner', {
      'Destination': navItem.destination,
      'Resource ID': navItem.resourceId,
      'Time Left In Trial': expired ? 0 : Math.round((now - trialExpiration) / 1000),
    });
  },
  signUp(store, method) {
    hltAnalytics('Localytics Registered', {
      'Method Name': method,
    });

    dataLayerEvent('TriggerGaEvent', {'category': `${store.getters['app/bundleIdentifier']}`, 'action': 'Sign Up', 'label': 'Wux', 'value': method});
  },
  updatedCreditCard() {
    hltAnalytics('Update Credit Card Clicked');
  },
  updatedJourneyDate(date, type) {
    hltAnalytics('Updated Journey Date', {
      'Date': date,
      'Date Type': type,
    });
  },
  viewedAttachment(attachment, resource) {
    hltAnalytics('Viewed Attachment', {
      'Attachment ID': attachment.id,
      'Attachment Title': attachment.assetTitle,
      'Attachment Resource ID': resource.id,
      'Attachment Resource Type': resource.type,
    });
  },
  viewedCart() {
    hltAnalytics('Viewed Cart');
  },
  viewedDashboard(navItem) {
    hltAnalytics('Viewed Dashboard', {
      'Nav Item Name': navItem?.name,
    });
  },
  viewedMyProgress() {
    hltAnalytics('Viewed My Progress');
  },
  viewedInformationalProgressModal() {
    hltAnalytics('Viewed Informational Progress Modal');
  },
  viewedMnemonic(category, mnemonic) {
    hltAnalytics('Viewed Mnemonic', {
      'Asset ID': mnemonic.id,
      'Asset Name': mnemonic.title,
      'Category ID': category.id,
      'Category Name': category.name,
    });
  },
  viewedRecommendedItemFromAsset(params) {
    hltAnalytics('Viewed Recommended Item from Asset', {
      'Asset Type': params.assetType,
      'Asset ID': params.assetId,
      'Asset Title': params.assetTitle,
      'Referring Asset ID': params.referringAssetId,
      'Referring Asset Title': params.referringAssetTitle,
      'Referring Asset Type': params.referringAssetType,
    });
  },
  viewedTransitionMessage(params) {
    hltAnalytics('Viewed Transition Message', {
      'Prompt Name': params.promptName,
    });
  },
  viewedTour(params) {
    hltAnalytics('Viewed Tour', {
      'Description': params.description,
      'Step Number': params.stepNumber,
      'Step Text': params.stepText,
      'Type': params.type,
    });
  },
};

const onboardingEvents = {
  answeredOnboardingQuestion(params) {
    hltAnalytics('Answered Onboarding Question', params);
  },
  finishedOnboardingCategory(id) {
    hltAnalytics('Finished Onboarding Category', { 'Category Id' : id });
  },
  savedGraduationDate() {
    hltAnalytics('Saved Grad Date');
  },
  selectedBeginFromOnboardingSummary() {
    hltAnalytics('Selected Begin from Onboarding Summary Page');
  },
  selectedBeginFromOnboardingWelcome() {
    hltAnalytics('Selected Begin from Onboarding Welcome Page');
  },
  selectedGraduationStatus(type) {
    const status = type === 'graduation' ? 'Yes' : 'No';
    hltAnalytics('Selected Grad Status', { 'Selected Status': status });
  },
  selectedPreviousFromOnboardingQuestion(params) {
    hltAnalytics('Selected Previous from Onboarding Question', params);
  },
  selectedPreviousFromOnboardingSummary() {
    hltAnalytics('Selected Previous from Onboarding Summary Page');
  },
  startedOnboardingCategory(id) {
    hltAnalytics('Started Onboarding Category', { 'Category Id' : id });
  },
  viewedGraduationDate() {
    hltAnalytics('Viewed Grad Date Onboarding Screen');
  },
  viewedGraduationStatus() {
    hltAnalytics('Viewed Grad Status Onboarding Screen');
  },
  viewedOnboardingFlashcard(id) {
    hltAnalytics('Viewed Onboarding Flashcard', { 'Flashcard Id' : id });
  },
  viewedOnboardingSummary() {
    hltAnalytics('Viewed Onboarding Summary Page');
  },
};

const preferredPOEvents = {
  dismissedPreferredSubscriptionScreen() {
    hltAnalytics('Dismissed Preferred Subscription Screen');
  },
  viewedBenefit(description) {
    hltAnalytics('Viewed Benefit', {
      'Description': description,
    });
  },
  viewedMembershipOptions() {
    hltAnalytics('Viewed Membership Options');
  },
  viewedPreferredSubscriptionScreen(type, value) {
    hltAnalytics('Viewed Preferred Subscription Screen', {
      'Source Type': type,
      'Source Value': value,
    });
  },
};

const profileEvents = {
  viewedProfileScreen() {
    hltAnalytics('Viewed Profile Screen');
  },
  resetProgress({ status }) {
    hltAnalytics('Clicked on Reset Your Progress', {
      'Status': status,
    });
  },
  resetSimProgress({ status, simulationName, simulationId }) {
    hltAnalytics('Clicked on Reset Simulations Progress', {
      'Simulation ID': simulationId,
      'Simulation Name': simulationName,
      'Status': status,
    });
  },
};

const questionOfTheDayEvents = {
  selectedQOTDWidget(params) {
    hltAnalytics('Selected QOTD Widget', {
      'State': params.state,
      'Dashboard ID': params.dashboardId,
      'Widget Title': params.widgetTitle,
      'Asset ID': params.assetId,
    });
  },
};

const quizEvents = {
  continuedQuiz(store, category) {
    if(store.getters['categoryTypes/userQuizCategoryType'] && store.getters['categoryTypes/userQuizCategoryType'].id == category.categoryTypeId) {
      hltAnalytics('Continued Custom Quiz', {'Quiz Category ID': category.id});
    } else {
      hltAnalytics('Continued Quiz', {'Quiz Category ID': category.id});
    }
  },
  createdUserQuiz(name, review, number) {
    hltAnalytics('Created User Quiz', {
      'User Quiz Name': name,
      'Review After Finish': review,
      'Number of Flashcards': number,
    });
  },
  dismissedQuestionCountModal(action) {
    hltAnalytics('Dismissed Question Count Modal', {
      'Action': action,
    });
  },
  reviewedQuiz(store, category) {
    if(store.getters['categoryTypes/userQuizCategoryType'] && store.getters['categoryTypes/userQuizCategoryType'].id == category.categoryTypeId) {
      hltAnalytics('Reviewed Custom Quiz', {'Quiz Category ID': category.id});
    } else {
      hltAnalytics('Reviewed Quiz', {'Quiz Category ID': category.id});
    }
  },
  restartedQuiz(store, category) {
    if(store.getters['categoryTypes/userQuizCategoryType'] && store.getters['categoryTypes/userQuizCategoryType'].id == category.categoryTypeId) {
      hltAnalytics('Restarted Custom Quiz', {'Quiz Category ID': category.id});
    } else {
      hltAnalytics('Restarted Quiz', {'Quiz Category ID': category.id});
    }
  },
  selectedQuestionLibrary(selectedTags) {
    hltAnalytics('Selected Question Library', {
      'Selected Tags': JSON.stringify(selectedTags),
    });
  },
  selectedCategories(selectedCategories) {
    hltAnalytics('Selected Categories', {
      'Category Names': JSON.stringify(selectedCategories.map(category => category.name)),
      'Category IDs': JSON.stringify(selectedCategories.map(category => category.id)),
    });
  },
  startedQuiz(store, category) {
    if(store.getters['categoryTypes/userQuizCategoryType'] && store.getters['categoryTypes/userQuizCategoryType'].id == category.categoryTypeId) {
      hltAnalytics('Started Custom Quiz', {'Quiz Category ID': category.id});
    } else {
      hltAnalytics('Started Quiz', {'Quiz Category ID': category.id});
    }
  },
  tappedSelectSubjects(subCategories) {
    hltAnalytics('Tapped Select Subjects', {
      'Sub-Category Names': JSON.stringify(subCategories.map(subCategory => subCategory.name)),
      'Sub-Category IDs': JSON.stringify(subCategories.map(subCategory => subCategory.id)),
    });
  },
};

const rateLimitEvents = {
  selectedRateLimitWidget({ dashboard, widget, destination }) {
    hltAnalytics('Selected Rate Limit Widget', {
      'Dashboard ID': dashboard.id,
      'Dashboard Title': dashboard.name,
      'Widget Title': widget.name,
      'Destination': destination,
    });
  },
  selectedRateLimitCounter({ creditsLeft, source }) {
    hltAnalytics('Selected Rate Limit Counter', {
      'Source Type': source.type,
      'Source ID': source.id,
      'Credits': creditsLeft,
    });
  },
  viewedRateLimitModal({ flashcardId, maxCredits }) {
    hltAnalytics('Viewed Rate Limit Modal', {
      'Source Type': 'Flashcard',
      'Source ID': flashcardId,
      'Rate Limit Quantity': maxCredits,
    });
  },
  dismissedRateLimitModal({ flashcardId, maxCredits }) {
    hltAnalytics('Dismissed Rate Limit Modal', {
      'Source Type': 'Flashcard',
      'Source ID': flashcardId,
      'Rate Limit Quantity': maxCredits,
    });
  },
};

const searchEvents = {
  completedSearch(query, navItem, route, store) {
    let resourceId, resourceType;
    if (route.params && route.params.categoryId) {
      const category = store.getters['categories/categories'][route.params.categoryId];
      if (category) {
        resourceId = category.id;
        resourceType = 'Category';
      }
    } else {
      resourceId = navItem.resourceId;
      resourceType = resourceId && 'Category Type';
    }
    hltAnalytics('Completed Search', {
      'Query': query,
      'Resource ID': resourceId,
      'Resource Type': resourceType,
      'Destination': navItem.destination,
      'Nav Item Name': navItem.name,
      'Nav Item ID': navItem.id,
    });
  },
  openedSearch(navItem, route, store) {
    let resourceId, resourceType;
    if (route.params && route.params.categoryId) {
      const category = store.getters['categories/categories'][route.params.categoryId];
      if (category) {
        resourceId = category.id;
        resourceType = 'Category';
      }
    } else {
      resourceId = navItem.resourceId;
      resourceType = resourceId && 'Category Type';
    }
    hltAnalytics('Opened Search', {
      'Resource ID': resourceId,
      'Resource Type': resourceType,
      'Destination': navItem.destination,
      'Nav Item Name': navItem.name,
      'Nav Item ID': navItem.id,
    });
  },
  selectedSearchTab(type) {
    hltAnalytics('Selected Search Tab', {
      'Title': type,
    });
  },
  viewedSearchPage() {
    hltAnalytics('Viewed Search Page');
  },
  viewedSearchResult(type, id) {
    hltAnalytics('Viewed Search Result', {
      'Asset Type': type,
      'Asset ID': id,
    });
  },
  completedSuggestedSearch(params) {
    hltAnalytics('Completed Suggested Search', {
      'Dashboard Title': params.dashboard?.name,
      'Dashboard ID': params.dashboard?.id,
      'Query': params.query,
    });
  },
};

const suggestATopicEvents = {
  selectedMenuItem({ navGroup, title, destination, resource }) {
    hltAnalytics('Selected Menu Item', {
      'Nav Group': navGroup,
      'Title': title,
      'Destination': destination,
      'Resource': resource,
    });
  },
  clickedHyperlink({ source, destination }) {
    hltAnalytics('Clicked Hyperlink', {
      'Source': source,
      'Link Destination': destination,
    });
  },
};

const topicEvents = {
  nextTopic() {
    hltAnalytics('Next Topic');
  },
  previousTopic() {
    hltAnalytics('Previous Topic');
  },
  viewedSubtopic({ topic, category, parent, root, source }) {
    let categoryId = category ? category.id : 'Search';
    let categoryName = category ? category.name : 'Search';
    hltAnalytics('Viewed Subtopic', {
      'Asset ID': topic.id,
      'Asset Path': topic.path,
      'Asset Title': topic.assetTitle,
      'Asset Type': 'Subtopic',
      'Category ID': categoryId,
      'Category Name': categoryName,
      'Parent Asset ID': parent.id,
      'Parent Asset Title': parent.title,
      'Root Asset ID': root.id,
      'Root Asset Title': root.title,
      'Source ID': source?.sourceId,
      'Source Type': source?.sourceType,
      'Source Category': source?.sourceCategory,
      'Source Category ID': source?.sourceCategoryId,
    });
  },
  viewedTopic({ topic, category, parentType, parentId, parentTitle, source }) {
    const params = {
      'Asset ID': topic.id,
      'Asset Path': topic.path,
      'Asset Title': topic.assetTitle,
      'Asset Type': 'Topic',
      'Root Asset ID': topic.id,
      'Root Asset Title': topic.assetTitle,
      'Source ID': source?.sourceId,
      'Source Type': source?.sourceType,
      'Source Category': source?.sourceCategory,
      'Source Category ID': source?.sourceCategoryId,
    };

    if (parentType && parentId) {
      params['Parent Asset Type'] = parentType;
      params['Parent Asset ID'] = parentId;
      params['Parent Asset Title'] = parentTitle;
    } else {
      let categoryId = category ? category.id : 'Search';
      let categoryName = category ? category.name : 'Search';
      params['Category ID'] = categoryId;
      params['Category Name'] = categoryName;
    }

    hltAnalytics('Viewed Topic', params);
  },
};

const widgetEvents = {
  showedWidget(id) {
    hltAnalytics('Enabled Widget', id);
  },
  hideWidget(id) {
    hltAnalytics('Disabled Widget', id);
  },
  // Recommended Today Widget
  viewedRecommendedItemFromWidget(params) {
    hltAnalytics('Viewed Recommended Item from Widget', {
      'Asset Type': params.assetType,
      'Asset ID': params.assetId,
      'Asset Title': params.assetTitle,
      'Dashboard ID': params.dashboardId,
      'Dashboard Title': params.dashboardTitle,
    });
  },
  viewedAllRecommendedItemsInWidget(params) {
    hltAnalytics('Viewed All Recommended Items from Widget', {
      'Dashboard ID': params.dashboardId,
      'Dashboard Title': params.dashboardTitle,
    });
  },
  // Today Widget
  adjustedDatePickerTodayWidget() {
    hltAnalytics('Adjusted Date Picker in Today Widget');
  },
  completedDailyGoal(dailyGoal) {
    hltAnalytics('Completed Goal', {
      'Number of Questions to reach goal': dailyGoal,
    });
  },
  setDailyGoal(destination) {
    hltAnalytics('Set Daily Goal', {
      'Destination': destination,
    });
  },
  startedPracticeQuestionsFromToday() {
    hltAnalytics('Started Practice Questions From Today Widget');
  },
  viewedDailyProgressTodayWidget(daysFromToday, formattedDateViewed) {
    hltAnalytics('Viewed Progress in Today Widget', {
      'Days from current date': daysFromToday,
      'Date Viewed': formattedDateViewed,
    });
  },
  // Block/Showcase widgets
  viewedCard(card, deck = null, widget = null) {
    hltAnalytics('Viewed Card', {
      'Widget ID': widget?.id,
      'Widget Type': widget?.widgetType,
      'Widget Title': widget?.name,
      'Deck ID': deck?.id,
      'Deck Type': deck?.deckType,
      'Deck Title': deck?.title,
      'Card ID': card.id,
      'Card Title': card.title,
      'Card Asset Type': card.assetType,
      'Card Asset ID': card.assetId,
    });
  },
  viewedDeck(deck, card = null, widget = null, dashboard = null) {
    hltAnalytics('Viewed Deck', {
      'Widget ID': widget?.id,
      'Widget Type': widget?.widgetType,
      'Widget Title': widget?.name,
      'Deck ID': deck.id,
      'Deck Type': deck.deckType,
      'Deck Title': deck.title,
      'Card ID': card?.id,
      'Card Title': card?.title,
      'Card Asset Type': card?.assetType,
      'Card Asset ID': card?.assetId,
      'Dashboard ID': dashboard?.id,
      'Dashboard Title': dashboard?.name,
    });
  },
  viewedWidget(widget, card = null, deck = null, dashboard = null) {
    hltAnalytics('Viewed Widget', {
      'Widget ID': widget.id,
      'Widget Type': widget.widgetType,
      'Widget Title': widget.name,
      'Deck ID': deck?.id,
      'Deck Type': deck?.deckType,
      'Deck Title': deck?.title,
      'Card ID': card?.id,
      'Card Title': card?.title,
      'Card Asset Type': card?.assetType,
      'Card Asset ID': card?.assetId,
      'Dashboard ID': dashboard?.id,
      'Dashboard Title': dashboard?.name,
    });
  },
  // Possibility of passing widget
  calculatedInitialScore(questionsTotal, possibility) {
    hltAnalytics('Calculated Initial Score', {
      'Number Of Questions': questionsTotal,
      'Possibility Percentage': possibility,
    });
  },
  viewedBetaScreen() {
    hltAnalytics('Viewed Beta Screen');
  },
  viewedScoreFactor(scoreFactor) {
    hltAnalytics('Viewed Score Factor', {
      'Score Factor': scoreFactor,
    });
  },
  // Upgrade/Buy Widget
  tappedBuyWidget(params) {
    hltAnalytics('Tapped on Buy Widget', {
      'Widget ID': params.widgetId,
      'Widget Title': params.widgetTitle,
    });
  },
};

const discussionEvents = {
  createdDiscussionComment(question, reply, isQOTD) {
    hltAnalytics('Created Discussion Comment', {
      'Question ID': question.id,
      'Reply': reply,
      'QOTD': isQOTD,
    });
  },
  ratedDiscussion(params) {
    hltAnalytics('Rated Discussion', {
      'Asset ID': params.asset.id,
      'Asset Title': null,
      'Asset Type': 'V3Flashcard',
      'Category ID': params.category?.id,
      'Category Name': params.category?.name,
      'Rated': params.rating,
      'Parent Discussion ID': params.parentDiscussionId,
      'Discussion ID': params.discussionId,
    });
  },
  removedDiscussionRating(params) {
    hltAnalytics('Removed Discussion Rating', {
      'Asset ID': params.asset.id,
      'Asset Title': null,
      'Asset Type': 'V3Flashcard',
      'Category ID': params.category?.id,
      'Category Name': params.category?.name,
      'Rated': params.rating,
      'Parent Discussion ID': params.parentDiscussionId,
      'Discussion ID': params.discussionId,
    });
  },
  viewedDiscussionPage() {
    hltAnalytics('View Discussion Page');
  },
  viewedDiscussionReplies(source) {
    hltAnalytics('Viewed Replies', {
      'Source Type': source.type,
      'Source ID': source.id,
      'Source Category': source.category,
      'Source Category ID': source.categoryId,
    });
  },
  viewedPreviousDiscussion(discussion) {
    hltAnalytics('View Previous Discussion', {
      'Discussion ID': discussion.id,
    });
  },
};

const avEvents = {
  endedTrack(data) {
    hltAnalytics('Ended AV Track', data);
  },
  pauseTrack(data) {
    hltAnalytics('Tapped Pause Button on AV Track', data);
  },
  playTrack(data) {
    hltAnalytics('Tapped Play Button on AV Track', data);
  },
  jumpForward(data) {
    hltAnalytics('Jumped AV Forward', data);
  },
  jumpBackward(data) {
    hltAnalytics('Jumped AV Backwards', data);
  },
  viewedTrack(data) {
    hltAnalytics('Viewed AV Track', data);
  },
};

const ratingEvents = {
  ratedAsset(data) {
    hltAnalytics('Rated Asset', data);
  },
  removedAssetRating(data) {
    hltAnalytics('Removed Asset Rating', data);
  },
};

const sharingEvents = {
  copiedShareLink(data) {
    hltAnalytics('Copied Share Link', data);
  },
  sharedAsset(data) {
    hltAnalytics('Shared Asset', data);
  },
};

const studyGoalEvents = {
  viewedEditStudyGoalFromWidget() {
    hltAnalytics('Viewed Edit Study Goal From Widget');
  },
  updatedStudyGoalFromModal(data) {
    hltAnalytics('Updated Study Goal From Modal', data);
  },
  savedStudyGoalFromModal(data) {
    hltAnalytics('Saved Study Goal From Modal', data);
  },
  viewedSetCustomStudyGoalFromModal() {
    hltAnalytics('Viewed Set Custom Study Goal From Modal');
  },
  viewedEditStudyGoalFromProfile() {
    hltAnalytics('Viewed Edit Study Goal From Profile');
  },
  setStudyGoal() {
    hltAnalytics('Set Study Goal');
  },
};

const libraryEvents = {
  viewedLibraryAsset(params) {
    hltAnalytics('Viewed Library Asset', {
      'Asset ID': params.assetId,
      'Asset Type': params.assetType,
      'Asset Title': params.assetTitle,
      'Parent Tag': params.parentTag,
      'Child Tag': params.childTag,
      'Asset Location': params.assetLocation,
    });
  },
  viewedAllFromLibrary(tag, parentTag) {
    hltAnalytics('Viewed All From Library', {
      'Parent Tag': parentTag,
      'Child Tag': tag,
    });
  },
  sortedLibrary(sortType) {
    hltAnalytics('Used Library Sort Function', {
      'Type Of Sort': sortType,
    });
  },
};

const startWidgetEvents = {
  viewedStartWidget() {
    hltAnalytics('Viewed Start Widget');
  },
  selectedStartWidget() {
    hltAnalytics('Selected Start Widget');
  },
};

const modalEvents = {
  viewedModal(params = {}) {
    hltAnalytics('Viewed Modal', {
      'Modal Title': params.title,
      'Modal Text': params.text,
      'Modal Type': params.type,
    });
  },
  viewedUpgradeScreen(source, purchaseOrder) {
    hltAnalytics('Viewed Upgrade Screen', {
      'Source Type': source.type,
      'Source Value': source.value,
      'Purchase Order ID': purchaseOrder?.id,
      'Purchase Order Name': purchaseOrder?.name,
    });
  },
};

const simulationEvents = {
  // This only works for Multiple Choice Questions at the moment
  updateSimulationAnswer(params) {
    const { simulation, section, flashcard, answerIds } = params;
    const answers = flashcard.answers;
    const correctAnswers = answers.filter((answer) => answer.correct);
    const chosenAnswers = answers.filter((answer) => answerIds.includes(answer.id));
    const correct = correctAnswers[0]?.id === chosenAnswers[0]?.id;

    hltAnalytics('Simulation Update Answer', {
      'Simulation Id': simulation.id,
      'Simulation Name': simulation.name,
      'Section Id': section.id,
      'Section Name': section.name,
      'Flashcard Id': flashcard.id,
      'Flashcard Type': flashcard.type,
      'Answer Was': correct ? 'Correct' : 'Incorrect',
      'Chosen Answer Ids': answerIds,
      'Chosen Answer Content': chosenAnswers.map((answer) => answer.content),
      'Correct Answer Ids': correctAnswers.map((answer) => answer.id),
      'Correct Answer Content': correctAnswers.map((answer) => answer.content),
    });
  },

  simulationNavigation(params) {
    const { simulation, section, flashcardOriginId, flashcardDestinationId, buttonType, answerUpdated } = params;
    hltAnalytics('Simulation Navigation', {
      'Simulation Id': simulation.id,
      'Simulation Name': simulation.name,
      'Section Id': section.id,
      'Section Name': section.name,
      'Flashcard Origin Id': flashcardOriginId,
      'Flashcard Destination Id': flashcardDestinationId,
      'Button Type': buttonType,
      'Answer Updated': answerUpdated ? 'yes' : 'no',
    });
  },

  simulationFlaggedQuestion(params) {
    const { simulation, section, flashcard, flagged } = params;
    hltAnalytics('Simulation Flagged Question', {
      'Simulation Id': simulation.id,
      'Simulation Name': simulation.name,
      'Section Id': section.id,
      'Section Name': section.name,
      'Flashcard Id': flashcard.id,
      'Flag': flagged ? 'added' : 'removed',
    });
  },

  simulationStrikeThrough(params) {
    const { simulation, section, flashcard, answer, strikeThrough } = params;
    hltAnalytics('Simulation Strike Through', {
      'Simulation Id': simulation.id,
      'Simulation Name': simulation.name,
      'Section Id': section.id,
      'Section Name': section.name,
      'Flashcard Id': flashcard.id,
      'Answer Id': answer.id,
      'Answer Content': answer.content,
      'Strike Through': strikeThrough ? 'added' : 'removed',
    });
  },

  simulationHighlight(params) {
    const { simulation, section, flashcard, highlightContainer } = params;
    hltAnalytics('Simulation Highlight', {
      'Simulation Id': simulation.id,
      'Simulation Name': simulation.name,
      'Section Id': section.id,
      'Section Name': section.name,
      'Flashcard Id': flashcard.id,
      'Highlight Container': highlightContainer,
    });
  },

  simulationCategoryState(params) {
    hltAnalytics('Simulation Category State', {
      'Section Id': params.categoryId,
      'Category Status Id': params.categoryStatusId,
      'Category Status Name': params.categoryStatusName,
    });
  },

  simulationStart(params) {
    hltAnalytics('Simulation Start', {
      'Simulation Id': params.simulationId,
      'Simulation Name': params.simulationName,
    });
  },

  simulationFinish(params) {
    hltAnalytics('Simulation Finish', {
      'Simulation Id': params.simulationId,
      'Simulation Name': params.simulationName,
    });
  },
};

export default {
  ...assetTrayEvents,
  ...avEvents,
  ...biodigitalEvents,
  ...bookmarkEvents,
  ...caseStudyEvents,
  ...catalogEvents,
  ...categoryEvents,
  ...checklistEvents,
  ...delayedPurchaseEvents,
  ...discussionEvents,
  ...flashcardEvents,
  ...learningModuleEvents,
  ...libraryEvents,
  ...miscellaneousEvents,
  ...modalEvents,
  ...onboardingEvents,
  ...preferredPOEvents,
  ...profileEvents,
  ...qotdEvents,
  ...questionOfTheDayEvents,
  ...quizEvents,
  ...rateLimitEvents,
  ...ratingEvents,
  ...searchEvents,
  ...sharingEvents,
  ...studyGoalEvents,
  ...suggestATopicEvents,
  ...topicEvents,
  ...widgetEvents,
  ...simulationEvents,
  ...startWidgetEvents,
};
